'use client';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { FC, useState } from 'react';
import { Button } from './ui/button';
import { UploadAvatar } from './upload-avatar';

type PCMemberProps = {
  title?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  affiliation: string;
  country?: string;
  image?: string;
  homepage?: string;
  email?: string;
  // Color props for theming
  textColor?: string;
  textSecondaryColor?: string;
  affiliationColor?: string;
  backgroundColor?: string;
  linkColor?: string;
};

export const PCMember: FC<PCMemberProps> = ({
  firstName,
  lastName,
  affiliation,
  country,
  image,
  title,
  homepage,
  textColor = '#1F2937',
  textSecondaryColor = '#6B7280',
  affiliationColor = '#9CA3AF',
  backgroundColor = '#F1F5F9',
  linkColor = '#F59E0B'
}) => {
  const content = (
    <div className='flex flex-row md:flex-col items-center space-y-3 gap-4 md:gap-0'>
      <UploadAvatar
        className='h-20 w-20'
        src={image}
        alt={`${title} ${firstName} ${lastName}`}
        fallback={
          (firstName?.charAt(0) || '').toUpperCase() + (lastName?.charAt(0) || '').toUpperCase()
        }
        fallbackStyle={{ backgroundColor }}
      />
      <div className='md:text-center space-y-1'>
        <div className='font-medium' style={{ color: textColor }}>
          {title} {firstName} {lastName}
        </div>
        <div className='text-sm' style={{ color: affiliationColor }}>
          {country ? `${affiliation}, ${country}` : affiliation}
        </div>
      </div>
    </div>
  );

  if (homepage) {
    return (
      <a
        href={homepage}
        target='_blank'
        rel='noopener noreferrer'
        className='cursor-pointer transition-all duration-200 hover:opacity-80 block'>
        <div className='flex flex-row md:flex-col items-center space-y-3 gap-4 md:gap-0'>
          <UploadAvatar
            className='h-20 w-20'
            src={image}
            alt={`${title} ${firstName} ${lastName}`}
            fallback={
              (firstName?.charAt(0) || '').toUpperCase() + (lastName?.charAt(0) || '').toUpperCase()
            }
            fallbackStyle={{ backgroundColor }}
          />
          <div className='md:text-center space-y-1'>
            <div className='font-medium' style={{ color: linkColor }}>
              {title} {firstName} {lastName}
            </div>
            <div className='text-sm' style={{ color: affiliationColor }}>
              {country ? `${affiliation}, ${country}` : affiliation}
            </div>
          </div>
        </div>
      </a>
    );
  }

  return content;
};

type PCMembersProps = {
  members: PCMemberProps[];
  // Color props for theming
  textColor?: string;
  textSecondaryColor?: string;
  affiliationColor?: string;
  backgroundColor?: string;
  primaryColor?: string;
  backgroundGradient?: string;
  linkColor?: string;
};

export const PCMembers: FC<PCMembersProps> = ({
  members,
  textColor = '#1F2937',
  textSecondaryColor = '#6B7280',
  affiliationColor = '#9CA3AF',
  backgroundColor = '#F1F5F9',
  primaryColor = '#3B82F6',
  backgroundGradient = 'rgba(255, 255, 255, 1)',
  linkColor = '#F59E0B'
}) => {
  const [showAll, setShowAll] = useState(false);

  // Estimate if content would overflow 24rem (384px)
  // Each member card is approximately 120px height including gap
  const estimatedHeight = Math.ceil(members.length / 4) * 120; // Assuming 4 columns on large screens
  const hasOverflow = estimatedHeight > 384;

  return (
    <div>
      <motion.div
        initial={{ maxHeight: hasOverflow ? '24rem' : 'fit-content' }}
        animate={{ maxHeight: showAll ? 'fit-content' : hasOverflow ? '24rem' : 'fit-content' }}
        transition={{ duration: 0.5 }}
        className={cn(
          'flex flex-wrap gap-4 w-full',
          showAll || !hasOverflow ? '' : 'overflow-hidden'
        )}
        style={{
          justifyContent: 'center',
          alignContent: 'flex-start'
        }}>
        {members
          .sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''))
          .map((member) => (
            <div
              key={`${member.firstName}_${member.lastName}`}
              className='w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)] xl:w-[calc(25%-0.75rem)] min-w-0'>
              <PCMember
                title={
                  member.title && (member.title.includes('Prof') || member.title.includes('Dr'))
                    ? member.title
                    : ''
                }
                firstName={member.firstName}
                lastName={member.lastName}
                affiliation={member.affiliation}
                country={member.country}
                homepage={member.homepage}
                image={member.image}
                textColor={textColor}
                textSecondaryColor={textSecondaryColor}
                affiliationColor={affiliationColor}
                backgroundColor={backgroundColor}
                linkColor={linkColor}
              />
            </div>
          ))}
      </motion.div>
      {hasOverflow && (
        <div className={cn('inset-x-0 flex justify-center -mt-9 relative', showAll ? 'mt-8' : '')}>
          <div
            className={cn('absolute inset-x-0 -top-32 bottom-1', showAll ? 'hidden' : '')}
            style={{
              background: `linear-gradient(to top, ${backgroundGradient} 0%, transparent 100%)`
            }}
          />
          <Button
            variant='link'
            size='lg'
            className='w-full sm:w-auto relative bottom-0'
            style={{ color: primaryColor }}
            onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show Less Members' : `Show All ${members.length} Members`}
          </Button>
        </div>
      )}
    </div>
  );
};
